import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import {
  MatDialogActions,
  MatDialogClose,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle,
} from '@angular/material/dialog';
import { MatError, MatFormField } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import {
  catchError,
  filter,
  map,
  merge,
  shareReplay,
  take,
  takeUntil,
  tap,
  throwError,
  timer,
} from 'rxjs';
import { BaseDialog } from '../../../../components/dialog/base-dialog';
import { ConfirmHandler } from '../../../../components/dialog/dialog.model';

export interface CreateClientDialogResult {
  name: string;
  nickname: string;
}

export interface CreateClientDialogData {
  onConfirm: ConfirmHandler<CreateClientDialogResult>;
}

@Component({
  selector: 'app-create-client-dialog',
  imports: [
    MatButton,
    MatDialogActions,
    MatDialogClose,
    MatDialogContent,
    MatDialogTitle,
    MatError,
    MatFormField,
    MatInput,
    MatProgressSpinner,
    ReactiveFormsModule,
  ],
  templateUrl: './create-client-dialog.component.html',
  styleUrl: './create-client-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CreateClientDialogComponent extends BaseDialog<CreateClientDialogData> {
  private readonly _dialogRef =
    inject<MatDialogRef<CreateClientDialogComponent>>(MatDialogRef);
  private readonly _fb = inject(FormBuilder);

  protected readonly form = this._fb.group({
    name: this._fb.control('', [Validators.required, Validators.maxLength(100)]),
    nickname: this._fb.control('', [Validators.required, Validators.maxLength(100)]),
  });

  protected readonly submitted = signal(false);
  protected readonly loading = signal(false);

  protected get controls() {
    return this.form.controls;
  }

  override handleConfirm(): void {
    if (this.form.invalid) {
      return;
    }

    const data = this.form.value as CreateClientDialogResult;
    const callback$ = this.data.onConfirm(data);

    const source$ = callback$.pipe(
      shareReplay(1),
      catchError((error) => {
        this.submitted.set(false);

        return throwError(() => error);
      })
    );

    const loading$ = merge(
      timer(1000).pipe(
        map(() => true),
        takeUntil(source$)
      ),

      source$.pipe(map(() => false))
    );

    loading$
      .pipe(
        tap((state) => this.loading.set(state)),
        filter((state) => !state),
        tap(() => this._dialogRef.close(true)),
        take(1)
      )
      .subscribe();
  }
}
